import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import moment from 'moment-timezone';
import { getVenueCalendar } from '../api';
import { userLogoutThunk } from './userSlice';
import Logger from '../utils/logger';

const EXPIRES_MS = 300000;
const getKey = (params) => [params.venue_id, params.num_seats, params.start_date, params.end_date].join('_');

const loadVenueInventoryThunk = createAsyncThunk(
  'venueInventory/loadVenueInventory',
  async ({ params }, { dispatch, rejectWithValue }) => {
    try {
      const response = await getVenueCalendar(params);
      return response;
    } catch (error) {
      if (error?.status === 419) {
        await dispatch(userLogoutThunk());
      }
      return rejectWithValue(error, { rejected: true });
    }
  }
);

const venueInventorySlice = createSlice({
  name: 'venueInventory',
  initialState: {
    isLoading: false,
    venueInventory: {},
    venueInventoryKey: '',
    cache: {},
  },
  reducers: {
    updateVenueInventory: (state, action) => {
      const key = action.payload;
      if (state.cache[key]) {
        state.venueInventory = state.cache[key];
        state.venueInventoryKey = key;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadVenueInventoryThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(loadVenueInventoryThunk.fulfilled, (state, action) => {
      state.isLoading = false;
      const { params } = action.meta.arg;
      const key = getKey(params);
      state.cache[key] = action.payload;
      state.cache[key].expires = moment().add(EXPIRES_MS, 'ms').valueOf();
    });
    builder.addCase(loadVenueInventoryThunk.rejected, (state, action) => {
      state.isLoading = false;
      const {
        config: { params }, data, headers, status, statusText,
      } = action.payload;
      state.error = {
        config: { params }, data, headers, status, statusText,
      };
    });
  },
});

const { updateVenueInventory } = venueInventorySlice.actions;
const getVenueInventoryThunk = ({ params, forcedReload = false }) => (dispatch, getState) => {
  const { venueInventory: slice } = getState();
  const key = getKey(params);
  if (slice.cache[key] && moment().isBefore(slice.cache[key].expires) && !forcedReload) {
    return dispatch(updateVenueInventory(key));
  }
  if (!slice.isLoading || forcedReload) {
    return dispatch(loadVenueInventoryThunk({ params }))
      .unwrap()
      .then(() => dispatch(updateVenueInventory(key)))
      .catch((err) => Logger.error('[ERROR] loadVenueInventoryThunk', err));
  }
  return Promise.resolve({});
};

export {
  loadVenueInventoryThunk,
  getVenueInventoryThunk,
};

export default venueInventorySlice.reducer;
